import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import clientService from '../services/clientService';
import pipelineService from '../services/pipelineService';
import { API_BASE_URL } from '../config/api';

const AddCandidateModal = ({ pipelineId, onClose, onSuccess, isClientMode = false, createOnly = false }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', linkedin: '',
    location: '', current_title: '', current_company: ''
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [firstStageId, setFirstStageId] = useState(null);

  useEffect(() => {
    if (pipelineId) loadPipelineStages();
  }, [pipelineId]);

  const loadPipelineStages = async () => {
    try {
      const pipeline = await pipelineService.getDetailed(pipelineId);
      if (pipeline.stages?.length > 0) {
        const sorted = [...pipeline.stages].sort((a, b) => a.position - b.position);
        setFirstStageId(sorted[0].id);
      }
    } catch (err) { console.error('Error loading pipeline stages:', err); }
  };

  useEffect(() => {
    if (activeTab === 'search') loadCandidates();
  }, [activeTab]);

  const loadCandidates = async () => {
    setSearching(true);
    try {
      if (isClientMode) {
        const data = await clientService.getPresentedCandidates();
        setSearchResults(data || []);
      } else {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/candidates`, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.ok ? await res.json() : [];
        setSearchResults(data || []);
      }
    } catch (err) { console.error('Error loading candidates:', err); }
    finally { setSearching(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      let newCandidate;
      if (isClientMode) {
        newCandidate = await clientService.createCandidate(formData);
      } else {
        const token = localStorage.getItem('authToken');
          const createRes = await fetch(`${API_BASE_URL}/api/candidates`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
          if (!createRes.ok) throw new Error('Failed to create candidate');
          newCandidate = await createRes.json();
      }
      if (newCandidate && firstStageId && !createOnly) {
        const addToken = localStorage.getItem('authToken');
          const addRes = await fetch(`${API_BASE_URL}/api/candidates/${newCandidate.id}/add-to-pipeline`, { method: 'POST', headers: { Authorization: `Bearer ${addToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ pipeline_id: pipelineId, stage_id: firstStageId }) });
          if (!addRes.ok) { const err = await addRes.json(); throw new Error(err.error || 'Failed to add'); }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creating candidate:', err);
      alert(err.response?.data?.error || 'Failed to add candidate');
    } finally { setSaving(false); }
  };

  const handleAddExisting = async () => {
    if (!selectedCandidate) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const addRes = await fetch(`${API_BASE_URL}/api/candidates/${selectedCandidate.id}/add-to-pipeline`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ pipeline_id: pipelineId, stage_id: firstStageId }) });
      if (!addRes.ok) { const err = await addRes.json(); throw new Error(err.error || 'Failed to add'); }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error adding candidate:', err);
      alert(err.response?.data?.error || 'Failed to add candidate. They may already be in this pipeline.');
    } finally { setSaving(false); }
  };

  const filteredCandidates = searchResults.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) ||
      c.current_company?.toLowerCase().includes(q) || c.current_title?.toLowerCase().includes(q) ||
      c.location?.toLowerCase().includes(q);
  });

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}
      className="bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">{createOnly ? 'Create Candidate' : 'Add Candidate'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{createOnly ? 'Add a new candidate to your database' : 'Create a new candidate or add an existing one to this pipeline'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        {!createOnly && (
        <div className="flex border-b px-2 flex-shrink-0">
          <button onClick={() => setActiveTab('create')}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === 'create' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Create New
          </button>
          <button onClick={() => setActiveTab('search')}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === 'search' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Search Existing
          </button>
        </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === 'create' ? (
            <form onSubmit={handleCreateSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="John Doe" required autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="+1 (555) 123-4567" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <input type="url" name="linkedin" value={formData.linkedin} onChange={handleChange} className={inputClass} placeholder="https://linkedin.com/in/johndoe" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Current Title</label>
                  <input type="text" name="current_title" value={formData.current_title} onChange={handleChange} className={inputClass} placeholder="Senior Engineer" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Current Company</label>
                  <input type="text" name="current_company" value={formData.current_company} onChange={handleChange} className={inputClass} placeholder="Tech Corp" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <input type="text" name="location" value={formData.location} onChange={handleChange} className={inputClass} placeholder="San Francisco, CA" />
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <button type="button" onClick={onClose} disabled={saving}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving || !formData.name.trim()}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                  {saving ? 'Saving...' : createOnly ? 'Create Candidate' : 'Create & Add'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, company..." className={`${inputClass} pl-8`} autoFocus />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {searching ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredCandidates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-xs text-gray-400">{searchQuery ? 'No candidates found' : 'No candidates available'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredCandidates.map(c => (
                      <div key={c.id} onClick={() => setSelectedCandidate(c)}
                        className={`px-3 py-2.5 cursor-pointer transition-colors flex items-center gap-3 ${
                          selectedCandidate?.id === c.id ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50 border-l-2 border-transparent'
                        }`}>
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">
                          {c.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-medium text-gray-900 truncate">{c.name}</p>
                            {isClientMode && c.source === 'client_created' && (
                              <span className="text-[8px] px-1 py-0.5 bg-purple-50 text-purple-600 rounded border border-purple-200">Yours</span>
                            )}
                            {isClientMode && c.source === 'presented' && (
                              <span className="text-[8px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200">Presented</span>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-500 truncate">
                            {c.current_title}{c.current_company ? ` · ${c.current_company}` : ''}{c.location ? ` · ${c.location}` : ''}
                          </p>
                        </div>
                        {selectedCandidate?.id === c.id && (
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <button onClick={onClose} disabled={saving}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleAddExisting} disabled={!selectedCandidate || saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                  {saving ? 'Adding...' : 'Add to Pipeline'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddCandidateModal;
